import { usePortfolioStore } from '../stores/portfolioStore';
import { useExchangeStore } from '../stores/exchangeStore';
import { useEffect, useMemo, useState } from 'react';

export interface PortfolioHeaderData {
  totalValue: number;
  totalAssets: number;
  connectedExchanges: number;
  btcDominance: number;
  change24hValue: number;
  change24hPercent: number;
  lastSyncTime?: string;
}

export function usePortfolioHeaderSelector(): PortfolioHeaderData {
  const { getUnifiedPositions, totalValue } = usePortfolioStore();
  const exchanges = useExchangeStore(state => state.exchanges);
  const [, setCryptoTableRefreshTick] = useState(0);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const onCryptoTableUpdated = () => setCryptoTableRefreshTick((v) => v + 1);
    window.addEventListener('ecotoneai_cryptoTable_updated', onCryptoTableUpdated);
    return () => window.removeEventListener('ecotoneai_cryptoTable_updated', onCryptoTableUpdated);
  }, []);

  const runtimeCryptoTable = typeof window !== 'undefined' && Array.isArray(window.cryptoTable)
    ? window.cryptoTable
    : undefined;
  const hasMeaningfulDelta = (left: number, right: number) => Math.abs(left - right) > 0.01;
  const runtimePriceBySymbol = useMemo(() => {
    const priceBySymbol = new Map<string, number>();
    if (Array.isArray(runtimeCryptoTable)) {
      runtimeCryptoTable.forEach((entry: any) => {
        const symbol = typeof entry?.symbol === 'string' ? entry.symbol.toUpperCase() : undefined;
        const price = typeof entry?.priceUsd === 'number'
          ? entry.priceUsd
          : (typeof entry?.currentPrice === 'number' ? entry.currentPrice : undefined);
        if (symbol && typeof price === 'number' && !isNaN(price)) {
          const existingPrice = priceBySymbol.get(symbol);
          if (existingPrice === undefined || existingPrice <= 0 || price > 0) {
            priceBySymbol.set(symbol, price);
          }
        }
      });
    }
    return priceBySymbol;
  }, [runtimeCryptoTable]);

  // 1. Total Assets (aggregated like UnifiedPortfolioTable, inclut on-chain)
  const unifiedPositions = getUnifiedPositions();
  const totalAssets = useMemo(() => {
    const map = new Map<string, any>();
    unifiedPositions.forEach((pos: any) => {
      if (pos.assetId && !map.has(pos.assetId)) {
        map.set(pos.assetId, { ...pos });
      }
    });
    return map.size;
  }, [unifiedPositions]);

  const computedTotalValue = useMemo(() => unifiedPositions.reduce((sum: number, p: any) => {
    if (typeof p.value === 'number' && !Number.isNaN(p.value)) {
      return sum + p.value;
    }
    if (typeof p.price === 'number' && typeof p.quantity === 'number') {
      return sum + (p.price * p.quantity);
    }
    if (typeof p.assetId === 'string' && typeof p.quantity === 'number') {
      const runtimePrice = runtimePriceBySymbol.get(p.assetId.toUpperCase());
      if (typeof runtimePrice === 'number') {
        return sum + (runtimePrice * p.quantity);
      }
    }
    return sum;
  }, 0), [runtimePriceBySymbol, unifiedPositions]);

  const normalizedStoredTotalValue = typeof totalValue === 'number' && !Number.isNaN(totalValue)
    ? totalValue
    : 0;
  const effectiveTotalValue = computedTotalValue > 0 && hasMeaningfulDelta(computedTotalValue, normalizedStoredTotalValue)
    ? computedTotalValue
    : (computedTotalValue > 0
      ? computedTotalValue
      : normalizedStoredTotalValue);

  // 2. Connected Exchanges
  const connectedExchanges = useMemo(() => Object.values(exchanges).filter((v: string) => v === 'CONNECTED').length, [exchanges]);

  // 3. BTC Dominance
  // Additionne la valeur de toutes les positions BTC (exchange, manuel, etc)
  const btcValue = unifiedPositions
    .filter((p: any) => p.assetId === 'BTC')
    .reduce((sum: number, p: any) => {
      if ('value' in p && typeof p.value === 'number') {
        return sum + p.value;
      } else if (typeof p.price === 'number' && typeof p.quantity === 'number') {
        return sum + p.price * p.quantity;
      } else if (typeof p.quantity === 'number') {
        const runtimePrice = runtimePriceBySymbol.get('BTC');
        return typeof runtimePrice === 'number' ? sum + (runtimePrice * p.quantity) : sum;
      }
      return sum;
    }, 0);
  const btcDominance = effectiveTotalValue > 0 ? (btcValue / effectiveTotalValue) * 100 : 0;

  

  
    // ...existing code...

  // 4. 24h Change
  const { change24hValue, change24hPercent } = useMemo(() => {
    let changeValue = 0;
    unifiedPositions.forEach((p: any) => {
      if (p.value && p.priceChange24h) {
        changeValue += p.value * (p.priceChange24h / 100);
      }
    });
    return {
      change24hValue: changeValue,
      change24hPercent: effectiveTotalValue > 0 ? (changeValue / effectiveTotalValue) * 100 : 0
    };
  }, [unifiedPositions, effectiveTotalValue]);

  // 5. Last Sync: read from localStorage (set by UnifiedPortfolioTable)
  let lastSyncTime: string | undefined = undefined;
  if (typeof window !== 'undefined') {
    const lastSyncStr = localStorage.getItem('ecotoneai_lastSync');
    if (lastSyncStr) {
      lastSyncTime = lastSyncStr;
    }
  }

  return {
    totalValue: effectiveTotalValue,
    totalAssets,
    connectedExchanges,
    btcDominance,
    change24hValue,
    change24hPercent,
    lastSyncTime
  };
}
